import { LitElement, css, html, type PropertyValues } from 'lit';
import { customElement, property } from 'lit/decorators.js';

import '@awesome.me/webawesome/dist/components/tooltip/tooltip.js';

import { scoreHoverLines, scoreLightTone, type TrustScoreReading } from '../helpers/trust-score-display.js';

/**
 * A company's published trust score as a traffic light: a dot in its band's colour (green 85+, amber 65-84, red below 65 and
 * when suppressed, grey with no score), with everything else, the number, the one state line and the date, in a tooltip shown
 * on hover and on keyboard focus. The dot is focusable and its aria-label is the tooltip's text, so the colour never carries
 * the meaning alone.
 */
@customElement('ns-trust-score-light')
export class NSTrustScoreLight extends LitElement {
  /** The score, or 'loading' / 'failed' while there is none to show. */
  @property({ attribute: false })
  reading: TrustScoreReading = 'loading';

  static styles = css`
    :host {
      display: inline-flex;
      flex: none;
      align-items: center;
    }

    .light {
      display: inline-block;
      box-sizing: border-box;
      width: var(--ns-trust-score-light-size, var(--wa-space-l));
      height: var(--ns-trust-score-light-size, var(--wa-space-l));
      border: 2px solid var(--ns-color-score-ring);
      border-radius: var(--wa-border-radius-circle);
      background: var(--ns-color-score-none);
      cursor: default;
    }

    .light:focus-visible {
      outline: 2px solid var(--wa-color-focus);
      outline-offset: 2px;
    }

    .light[data-tone='high'] {
      background: var(--ns-color-score-high);
    }

    .light[data-tone='mid'] {
      background: var(--ns-color-score-mid);
    }

    .light[data-tone='low'] {
      background: var(--ns-color-score-low);
    }

    wa-tooltip {
      --max-width: 240px;
    }
  `;

  // wa-tooltip points its anchor's aria-labelledby at itself, but its text sits in a hidden body while it is closed, so the dot
  // would be named "" whenever the tooltip is closed. The dot keeps its own aria-label, the same words, instead.
  protected override async updated(changed: PropertyValues<this>) {
    super.updated(changed);
    await this.renderRoot.querySelector('wa-tooltip')?.updateComplete;
    this.renderRoot.querySelector('.light')?.removeAttribute('aria-labelledby');
  }

  render() {
    const lines = scoreHoverLines(this.reading);
    return html`
      <span
        id="light"
        class="light"
        role="img"
        tabindex="0"
        data-tone=${scoreLightTone(this.reading)}
        aria-label=${lines.join(', ')}
      ></span>
      <wa-tooltip for="light" placement="top">
        ${lines.map(
          (line) => html`
            <div class="line">${line}</div>
          `,
        )}
      </wa-tooltip>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'ns-trust-score-light': NSTrustScoreLight;
  }
}
